import { useMutation, useQueryClient } from "@tanstack/react-query";

import { updateSoilSightProjectSettingsRefinements } from "services";

import { useIsAuthenticated } from "utils";

import { GET_SOIL_SIGHT_PROJECT, LIST_SOIL_SIGHT_PROJECTS, UPDATE_SOIL_SIGHT_PROJECT_SETTINGS } from "consts";

import { SoilSightProject, SoilSightProjectResponse, UpdateSettingsRefinementsBody } from "models";

type MutateArgs = {
  soilSightProjectId: string;
  body: UpdateSettingsRefinementsBody;
  /** Pass when assigning a field to a newly created project so the list cache can be patched
   * before GET_SOIL_SIGHT_PROJECT has been fetched for that project. */
  klimFarmId?: string;
};

const toCacheSettingsPatch = (body: UpdateSettingsRefinementsBody): Partial<SoilSightProjectResponse["settingsRefinements"]> => {
  const { projectionYears, ...rest } = body;

  return {
    ...rest,
    // projectionYears is non-null on the response; skip when the partial update omits it or sends null
    ...(!projectionYears ? {} : { projectionYears }),
  };
};

export const useUpdateSoilSightProjectSettingsRefinements = () => {
  const queryClient = useQueryClient();
  const isAuthenticated = useIsAuthenticated();

  return useMutation({
    mutationKey: [UPDATE_SOIL_SIGHT_PROJECT_SETTINGS],
    mutationFn: ({ soilSightProjectId, body }: MutateArgs) => {
      if (!isAuthenticated) return Promise.reject(new Error("Not Authorized"));
      return updateSoilSightProjectSettingsRefinements(soilSightProjectId, body);
    },
    // Patch instead of invalidate so users don't see a refetch flicker
    onSuccess: (_data, vars) => {
      const settingsPatch = toCacheSettingsPatch(vars.body);
      queryClient.setQueryData<SoilSightProjectResponse | undefined>([GET_SOIL_SIGHT_PROJECT, vars.soilSightProjectId], (old) =>
        old
          ? {
              ...old,
              settingsRefinements: { ...old.settingsRefinements, ...settingsPatch },
            }
          : old
      );
      // Patch fieldId directly in the project list cache so the displayed field name stays current without a refetch.
      if (vars.body.fieldId !== undefined) {
        const projectData = queryClient.getQueryData<SoilSightProjectResponse>([GET_SOIL_SIGHT_PROJECT, vars.soilSightProjectId]);
        // projectData?.farmId is undefined for brand-new projects (GET_SOIL_SIGHT_PROJECT not yet loaded),
        // so fall back to the caller-supplied klimFarmId to avoid silently dropping the patch.
        const resolvedKlimFarmId = projectData?.farmId ?? vars.klimFarmId;
        if (resolvedKlimFarmId) {
          queryClient.setQueryData<SoilSightProject[]>([LIST_SOIL_SIGHT_PROJECTS, resolvedKlimFarmId], (old) =>
            old ? old.map((f) => (f.id === vars.soilSightProjectId ? { ...f, fieldId: vars.body.fieldId ?? null } : f)) : old
          );
        }
      }
    },
  });
};
